// Curated candidate FAQ question catalog — client-owned static UI content.
//
// This is static content, so it lives on the frontend rather than behind an API: the Vuex actions
// position/getFaqQuestionCatalog and company/getCompanyFaqQuestionCatalog return buildPositionCatalog()
// / buildCompanySuggestionCatalog() directly. `normalized_text` is computed here via normalizeQuestionText.
//
// Provenance: a 167-row `unanswered_faq` review_item analysis spanning 2026-06-05..2026-07-17
// (companies: TTS 48%, Master Halco 24%, Blucor 19%, TCF 9%; ~140 substantive after filler /
// fragment / Spanish-noise removal). The phrasings below are cleaned-up canonical versions of those
// real questions. The "talk to a human / AI distrust" cluster (~8) is intentionally EXCLUDED: it is a
// product-level escalation concern, not a position FAQ.

export interface FaqCatalogQuestion {
  id: string;
  text: string;
  /** Filtering identity, computed via normalizeQuestionText. */
  normalized_text: string;
  answer_templates: string[];
  source: "curated";
  ask_count: number;
}

export interface FaqCatalogTheme {
  theme_key: string;
  theme_label: string;
  questions: FaqCatalogQuestion[];
}

export interface FaqCatalogResponse {
  catalog_version: string;
  themes: FaqCatalogTheme[];
}

/** Alias kept for the components that referenced the question type under this name. */
export type CatalogQuestion = FaqCatalogQuestion;

const MAX_ANSWER_TEMPLATES = 3;

// Bumped when the curated content below changes, so callers can cache-bust if they key on it.
const CATALOG_VERSION = "curated-2026-07-17";

/**
 * Filtering identity for a candidate FAQ question: lowercase, delete every character outside
 * `[a-z0-9 ]` (so "on-site" -> "onsite", the hyphen is removed not spaced), then collapse whitespace
 * runs and trim. Applied both to the curated catalog and to user-typed / persisted text so the two
 * can be compared; the golden-table spec pins the exact behaviour.
 */
export const normalizeQuestionText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

// id -> (canonical text, answer templates). Templates capped at MAX_ANSWER_TEMPLATES.
const CURATED_POSITION_QUESTIONS: Record<
  string,
  { text: string; answerTemplates: string[] }
> = {
  "q-success-90": {
    text: "What does success look like in this role in the first 90 days?",
    answerTemplates: [],
  },
  "q-team-and-report": {
    text: "How big is the team, and who would I report to?",
    answerTemplates: [],
  },
  "q-hear-back": {
    text: "When will I hear back, and what are the next steps?",
    answerTemplates: [
      "We follow up within a few days",
      "We follow up within a week",
    ],
  },
  "q-biggest-challenges": {
    text: "What are the biggest challenges of this position?",
    answerTemplates: [],
  },
  "q-typical-day": {
    text: "What does a typical day look like?",
    answerTemplates: [],
  },
  "q-team-structure": {
    text: "How big is the team and how is it structured?",
    answerTemplates: [],
  },
  "q-report-to": { text: "Who would I report to?", answerTemplates: [] },
  "q-solo-or-team": {
    text: "Would I work mostly on my own or with a team?",
    answerTemplates: [],
  },
  "q-when-start": { text: "When would I start?", answerTemplates: [] },
  "q-next-interview-format": {
    text: "Will the next interview be in person or virtual?",
    answerTemplates: [
      "In person",
      "Virtual",
      "Either, based on your preference",
    ],
  },
  "q-site-hybrid-remote": {
    text: "Is the role on-site, hybrid, or remote?",
    answerTemplates: [
      "Fully on-site",
      "Hybrid, a few days in office",
      "Remote friendly",
    ],
  },
  "q-shift-differential": {
    text: "Is there a pay differential for 2nd or 3rd shift?",
    answerTemplates: ["Yes, a shift differential applies", "No differential"],
  },
};

// (theme key, theme label, ordered question ids).
const POSITION_FAQ_THEMES: Array<{
  themeKey: string;
  themeLabel: string;
  questionIds: string[];
}> = [
  {
    themeKey: "top",
    themeLabel: "Top questions",
    questionIds: ["q-success-90", "q-team-and-report", "q-hear-back"],
  },
  {
    themeKey: "role-and-success",
    themeLabel: "Role & success",
    questionIds: ["q-success-90", "q-biggest-challenges", "q-typical-day"],
  },
  {
    themeKey: "team",
    themeLabel: "Team",
    questionIds: ["q-team-structure", "q-report-to", "q-solo-or-team"],
  },
  {
    themeKey: "hiring-process",
    themeLabel: "Hiring process",
    questionIds: ["q-hear-back", "q-when-start", "q-next-interview-format"],
  },
  {
    themeKey: "schedule-and-location",
    themeLabel: "Schedule & location",
    questionIds: ["q-site-hybrid-remote", "q-shift-differential"],
  },
];

// Company-wide FAQ suggestions (answered once at the company level, on the FAQ Library page).
const COMPANY_FAQ_SUGGESTIONS: Array<{
  id: string;
  text: string;
  answerTemplates: string[];
}> = [
  {
    id: "cq-about-company",
    text: "Can you tell me more about the company?",
    answerTemplates: [],
  },
  {
    id: "cq-company-age",
    text: "How long has the company been in business?",
    answerTemplates: [],
  },
  {
    id: "cq-how-quick-fill",
    text: "How quickly are you looking to fill this position?",
    answerTemplates: [],
  },
  {
    id: "cq-interview-feedback",
    text: "How did I do in this interview, and when will I get feedback?",
    answerTemplates: [
      "You will hear back within a few days",
      "We follow up within a week",
    ],
  },
  {
    id: "cq-recording-or-notes",
    text: "Will the hiring manager see a recording or just notes?",
    answerTemplates: [],
  },
];

const curatedQuestion = (
  id: string,
  text: string,
  answerTemplates: string[],
): FaqCatalogQuestion => ({
  id,
  text,
  normalized_text: normalizeQuestionText(text),
  answer_templates: answerTemplates.slice(0, MAX_ANSWER_TEMPLATES),
  source: "curated",
  ask_count: 0,
});

/** Curated position-theme catalog (Top / Role & success / Team / Hiring process / Schedule & location). */
export const buildPositionCatalog = (): FaqCatalogResponse => ({
  catalog_version: CATALOG_VERSION,
  themes: POSITION_FAQ_THEMES.map((theme) => ({
    theme_key: theme.themeKey,
    theme_label: theme.themeLabel,
    questions: theme.questionIds.map((id) =>
      curatedQuestion(
        id,
        CURATED_POSITION_QUESTIONS[id].text,
        CURATED_POSITION_QUESTIONS[id].answerTemplates,
      ),
    ),
  })),
});

/** Curated company-wide FAQ suggestions for the FAQ Library page, in the same response shape. */
export const buildCompanySuggestionCatalog = (): FaqCatalogResponse => ({
  catalog_version: CATALOG_VERSION,
  themes: [
    {
      theme_key: "company-suggestions",
      theme_label: "Company suggestions",
      questions: COMPANY_FAQ_SUGGESTIONS.map((q) =>
        curatedQuestion(q.id, q.text, q.answerTemplates),
      ),
    },
  ],
});

/** normalized_text -> answer templates, built from catalog themes. */
export type AnswerTemplateIndex = Map<string, string[]>;

/**
 * Index the catalog's answer templates by `normalized_text`. A question that appears in more than one
 * theme (e.g. a Top question that also lives in its home theme) carries an identical payload, so the
 * first occurrence wins.
 */
export const buildAnswerTemplateIndex = (
  themes: FaqCatalogTheme[],
): AnswerTemplateIndex => {
  const index: AnswerTemplateIndex = new Map();
  for (const theme of themes) {
    for (const question of theme.questions) {
      if (!index.has(question.normalized_text)) {
        index.set(question.normalized_text, question.answer_templates);
      }
    }
  }
  return index;
};

/**
 * Answer templates for a question phrasing (user-typed or persisted), or [] if the catalog has no such
 * question. The lookup normalizes the text the same way the catalog's `normalized_text` was built.
 */
export const answerTemplatesForText = (
  index: AnswerTemplateIndex,
  text: string,
): string[] => index.get(normalizeQuestionText(text)) ?? [];
